import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getHotelOffers } from './services/hotelSearchService'; // Corrected path
import { SimplifiedHotel } from '../chat/types'; // Corrected path
import { HotelType, OfferType } from '../chat/services/types/chatTypes'; // Corrected path, added OfferType here
import MapPreview from '../../components/MapPreview'; // Correct path to the new component

// TODO: Extract MapPreview to its own file for better reusability
// Temporarily copy the MapPreview component logic here or import if possible
// For now, let's assume we can import it (adjust path if needed)

// Placeholder function to map API response to our desired detail format
// You might need to adjust this based on the actual getHotelOffers response structure
function mapOfferResponseToDetails(hotelId: string, offerResponse: any): any | null {
  if (!offerResponse || !offerResponse[hotelId]) {
    console.warn(`No offer data found for hotel ${hotelId} in response:`, offerResponse);
    return null;
  }

  // Assuming the response for a single hotel ID looks similar to the multi-hotel one
  const hotelData = offerResponse[hotelId][0]?.hotel; // Access nested hotel data
  const offers = offerResponse[hotelId][0]?.offers; // Access offers array

  if (!hotelData) {
      console.warn(`No nested hotel object found for hotel ${hotelId}`);
      return null;
  }

  // --- Build the details object (adapt based on actual API response) ---
  return {
    id: hotelData.hotelId,
    name: hotelData.name || 'Hotel Name Unavailable',
    description: offers?.[0]?.room?.description?.text || 'Description not available.', // Example: Get description from first offer
    rating: hotelData.rating !== undefined ? parseFloat(String(hotelData.rating)) : undefined,
    city: hotelData.cityCode, // Example: Use cityCode if available
    latitude: hotelData.latitude,
    longitude: hotelData.longitude,
    media: hotelData.media || [], // Assuming media is an array
    amenities: hotelData.amenities || [], // Assuming amenities is an array
    // You might want to include room details from the 'offers' array here too
    roomInfo: offers // Pass offers to potentially display room types/prices
  };
}

// --- Room Option Sub-component (Adapted from ChatHotelCard) ---
// TODO: Potentially extract this to a shared component file
const RoomOptionDisplay = ({
  name,
  price,
  priceTotal,
  currency = 'USD',
  beds,
  description,
  boardType,
  cancellationDeadline
}: {
  name: string;
  price?: number;
  priceTotal?: string;
  currency?: string;
  beds?: string;
  description?: string;
  boardType?: string;
  cancellationDeadline?: string;
}) => {
  const formatDeadline = (deadline?: string) => {
    if (!deadline) return 'N/A';
    try {
      const date = new Date(deadline);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
      return deadline;
    }
  };

  return (
    <div className="p-3 rounded-lg bg-gray-800/50 border border-white/10 text-sm mb-3">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1 min-w-0 mr-3">
          <div className="font-semibold text-white/90 truncate">{name}</div>
          {beds && <div className="text-white/60 text-xs mt-0.5">Bed Type: {beds}</div>}
        </div>
        <div className="text-right flex-shrink-0">
          {priceTotal && currency ? (
             <div className="text-emerald-400 font-medium text-lg">{currency} {priceTotal}</div>
           ) : price !== undefined ? (
             <div className="text-emerald-400 font-medium text-lg">{currency} {price.toFixed(0)}</div>
           ) : (
             <div className="text-amber-400/80 text-xs">Price N/A</div>
           )}
          <div className="text-white/60 text-xs">per night</div>
        </div>
      </div>
      {description && (
        <p className="text-white/70 text-xs mt-1 mb-2.5">{description}</p>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/60 mt-2 border-t border-white/15 pt-2">
        {boardType && (
           <div className="flex items-center">
              {/* Icon Placeholder */} <span className="mr-1">🍽️</span>
              <span>{boardType.replace(/_/g, ' ')}</span>
           </div>
        )}
        {cancellationDeadline && (
           <div className="flex items-center">
              {/* Icon Placeholder */} <span className="mr-1">⏰</span>
             <span>Free cancel by: {formatDeadline(cancellationDeadline)}</span>
           </div>
        )}
      </div>
    </div>
  );
};
// --- End Room Option Sub-component ---

export default function HotelDetailPage() {
  const { hotelId } = useParams<{ hotelId: string }>();
  const [hotelDetails, setHotelDetails] = useState<any>(null); // Use a more specific type later
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hotelId) {
      setError("Hotel ID not found in URL.");
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      console.log(`Fetching details (via offers) for hotel ID: ${hotelId}`);
      try {
        // Use getHotelOffers to fetch data for this single hotel ID
        // Set dummy check-in/out dates, they might be required by the API even if not used for details
        const checkInDate = new Date();
        checkInDate.setDate(checkInDate.getDate() + 7);
        const checkOutDate = new Date(checkInDate);
        checkOutDate.setDate(checkOutDate.getDate() + 2);
        const formattedCheckIn = checkInDate.toISOString().split('T')[0];
        const formattedCheckOut = checkOutDate.toISOString().split('T')[0];

        const offerResponse = await getHotelOffers(
          [hotelId], // Pass only the current hotel ID
          formattedCheckIn,
          formattedCheckOut
          // Add other params like adults, rooms if needed by the function
        );

        console.log("Raw offer response for detail page:", offerResponse);

        // Map the response to the structure needed for display
        const details = mapOfferResponseToDetails(hotelId, offerResponse);

        if (details) {
          setHotelDetails(details);
        } else {
          setError('Could not retrieve details for this hotel.');
        }

      } catch (err) {
        setError('Failed to load hotel details. Please try again later.');
        console.error("Error fetching hotel details via offers:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [hotelId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
         <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500 mr-3"></div>
         Loading hotel details...
      </div>
    );
  }

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-red-400">Error: {error}</div>;
  }

  if (!hotelDetails) {
    return <div className="min-h-screen flex items-center justify-center text-white">Hotel details not found.</div>;
  }

  // --- Enhanced Rendering (Example) ---
  return (
    <div className="container mx-auto px-4 py-12 text-white">
      <h1 className="text-4xl font-bold mb-2 tracking-tight">{hotelDetails.name}</h1>
      {hotelDetails.city && <p className="text-lg text-gray-400 mb-6">{hotelDetails.city}</p>}

      {/* Placeholder for Image Gallery */}
      {hotelDetails.media && hotelDetails.media.length > 0 && (
        <div className="mb-8 bg-gray-800 rounded-lg overflow-hidden h-64 flex items-center justify-center">
          <img src={hotelDetails.media[0]?.uri} alt={`Image of ${hotelDetails.name}`} className="object-cover w-full h-full" onError={(e) => e.currentTarget.style.display = 'none'} />
          {/* TODO: Implement a proper image carousel */} 
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-2xl font-semibold mb-3">Description</h2>
          <p className="text-gray-300 leading-relaxed whitespace-pre-line">{hotelDetails.description}</p>
        </div>

        <div>
          {/* Replace Placeholder with MapPreview Component */}
          {hotelDetails.latitude && hotelDetails.longitude && (
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-2">Location</h3>
              {/* Use MapPreview component */}
              <MapPreview
                name={hotelDetails.name}
                latitude={hotelDetails.latitude}
                longitude={hotelDetails.longitude}
              />
            </div>
          )}

          {/* Amenities */}
          {hotelDetails.amenities && hotelDetails.amenities.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-2">Amenities</h3>
              <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                {hotelDetails.amenities.slice(0, 5).map((amenity: string, index: number) => (
                  <li key={index}>{amenity.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}</li>
                ))}
                {hotelDetails.amenities.length > 5 && <li>...and more</li>}
              </ul>
            </div>
          )}
        </div>
      </div>

       {/* --- Add section for Room Types/Offers --- */}
       {hotelDetails.roomInfo && Array.isArray(hotelDetails.roomInfo) && hotelDetails.roomInfo.length > 0 && (
         <div className="mt-8 pt-6 border-t border-white/10">
           <h2 className="text-2xl font-semibold mb-4">Available Rooms & Offers</h2>
           <div className="space-y-3">
             {hotelDetails.roomInfo.map((offer: any, index: number) => (
               // Map through the offers data provided in roomInfo
               <RoomOptionDisplay
                 key={offer.id || `room-${index}`} // Use offer ID if available
                 name={offer.room?.typeEstimated?.category || 'Unknown Room Type'}
                 price={offer.price?.total ? parseFloat(String(offer.price.total).replace(/[^0-9.-]+/g, "")) : undefined}
                 priceTotal={offer.price?.total}
                 currency={offer.price?.currency}
                 beds={offer.room?.typeEstimated?.bedType}
                 description={offer.room?.description?.text}
                 boardType={(offer as any).boardType} // Need to cast if not in OfferType initially
                 cancellationDeadline={(offer as any).policies?.cancellations?.[0]?.deadline}
               />
             ))}
           </div>
         </div>
       )}
       {/* --- End Room Types/Offers section --- */}

      <p className="text-xs text-gray-600 mt-12">Hotel ID: {hotelId}</p>
    </div>
  );
} 